/**
 * EARS 需求到任务的转换器
 * 将 EARS 格式的需求转换为可执行的开发任务
 */

import { EARSRequirement, EARSType } from './ears-parser';
import { Task, TaskPriority, TaskStatus } from '../../types';

export interface ConversionOptions {
  defaultPriority?: TaskPriority;
  defaultStatus?: TaskStatus;
  includeTests?: boolean;
  includeDocumentation?: boolean;
  estimateHours?: boolean;
}

export class EARSTaskConverter {
  private defaultOptions: ConversionOptions = {
    defaultPriority: 'medium',
    defaultStatus: 'todo',
    includeTests: true,
    includeDocumentation: true,
    estimateHours: true
  };
  
  /**
   * 将单个 EARS 需求转换为任务
   */
  convertRequirement(
    requirement: EARSRequirement, 
    options?: ConversionOptions
  ): Task[] {
    const opts = { ...this.defaultOptions, ...options };
    const tasks: Task[] = [];
    
    // 主实现任务
    const mainTask = this.createMainTask(requirement, opts);
    tasks.push(mainTask);
    
    // 测试任务
    if (opts.includeTests) {
      const testTask = this.createTestTask(requirement, opts);
      if (testTask) tasks.push(testTask);
    }
    
    // 文档任务
    if (opts.includeDocumentation) {
      const docTask = this.createDocumentationTask(requirement, opts);
      if (docTask) tasks.push(docTask);
    }
    
    // 特殊类型的额外任务
    const extraTasks = this.createExtraTasks(requirement, opts);
    tasks.push(...extraTasks);
    
    return tasks;
  }
  
  /**
   * 批量转换需求
   */
  convertRequirements(
    requirements: EARSRequirement[], 
    options?: ConversionOptions
  ): Task[] {
    const allTasks: Task[] = [];
    
    for (const req of requirements) {
      const tasks = this.convertRequirement(req, options);
      allTasks.push(...tasks);
    }
    
    // 设置任务位置
    allTasks.forEach((task, index) => {
      task.position = index;
    });
    
    return allTasks;
  }
  
  /**
   * 创建主实现任务
   */
  private createMainTask(req: EARSRequirement, opts: ConversionOptions): Task {
    const content = this.generateTaskContent(req);
    const priority = this.mapPriority(req.priority) || opts.defaultPriority!;
    
    const task: Task = {
      id: `task-${req.id}`,
      content,
      status: opts.defaultStatus!,
      priority,
      position: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
      isCurrent: false,
      tags: [...(req.tags || []), 'implementation', req.type],
      notes: `EARS Requirement: ${req.rawText || this.reconstructEARS(req)}`,
      section: this.getSection(req.type)
    };
    
    if (opts.estimateHours) {
      task.estimatedHours = this.estimateHours(req);
    }
    
    return task;
  }
  
  /**
   * 创建测试任务
   */
  private createTestTask(req: EARSRequirement, opts: ConversionOptions): Task | null {
    // 不想要的行为特别需要测试
    if (req.type === EARSType.UNWANTED || req.priority === 'must') {
      const content = `编写测试用例：${this.getTestDescription(req)}`;
      
      return {
        id: `test-${req.id}`,
        content,
        status: opts.defaultStatus!,
        priority: this.mapPriority(req.priority) || opts.defaultPriority!,
        position: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        isCurrent: false,
        tags: [...(req.tags || []), 'testing', req.type],
        notes: `测试 EARS 需求: ${req.id}`,
        section: '测试任务',
        estimatedHours: opts.estimateHours ? Math.ceil(this.estimateHours(req) * 0.3) : undefined,
        dependsOn: [`task-${req.id}`]
      };
    }
    
    return null;
  }
  
  /**
   * 创建文档任务
   */
  private createDocumentationTask(req: EARSRequirement, opts: ConversionOptions): Task | null {
    // 复杂需求需要详细文档
    if (req.type === EARSType.COMPLEX || req.priority === 'must') {
      const content = `更新文档：${req.system} - ${this.getSummary(req)}`;
      
      return {
        id: `doc-${req.id}`,
        content,
        status: opts.defaultStatus!,
        priority: 'low',
        position: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        isCurrent: false,
        tags: [...(req.tags || []), 'documentation'],
        notes: `记录 EARS 需求实现: ${req.id}`,
        section: '文档任务',
        estimatedHours: opts.estimateHours ? 2 : undefined,
        dependsOn: [`task-${req.id}`]
      };
    }
    
    return null;
  }
  
  /**
   * 创建额外任务
   */
  private createExtraTasks(req: EARSRequirement, opts: ConversionOptions): Task[] {
    const tasks: Task[] = [];
    
    // 事件驱动需求可能需要事件系统
    if (req.type === EARSType.EVENT_DRIVEN && req.when) {
      const eventTask: Task = {
        id: `event-${req.id}`,
        content: `实现事件监听器：${req.when}`,
        status: opts.defaultStatus!,
        priority: opts.defaultPriority!,
        position: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        isCurrent: false,
        tags: ['event-system', 'infrastructure'],
        notes: `为 ${req.system} 实现事件处理`,
        section: '基础设施',
        estimatedHours: opts.estimateHours ? 4 : undefined
      };
      tasks.push(eventTask);
    }
    
    // 状态驱动需求可能需要状态管理
    if (req.type === EARSType.STATE_DRIVEN && req.while) {
      const stateTask: Task = {
        id: `state-${req.id}`,
        content: `实现状态管理：${req.while}`,
        status: opts.defaultStatus!,
        priority: opts.defaultPriority!,
        position: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        isCurrent: false,
        tags: ['state-management', 'infrastructure'],
        notes: `为 ${req.system} 实现状态管理`,
        section: '基础设施',
        estimatedHours: opts.estimateHours ? 6 : undefined
      };
      tasks.push(stateTask);
    }
    
    return tasks;
  }
  
  /**
   * 生成任务内容
   */
  private generateTaskContent(req: EARSRequirement): string {
    const action = this.extractAction(req.shall);
    const context = this.getContext(req);
    
    return `${context}实现 ${req.system} ${action}`;
  }
  
  /**
   * 获取上下文描述
   */
  private getContext(req: EARSRequirement): string {
    const parts: string[] = [];
    
    if (req.when) parts.push(`当${req.when}时`);
    if (req.where) parts.push(`在${req.where}`);
    if (req.while) parts.push(`当${req.while}时`);
    if (req.if) parts.push(`如果${req.if}`);
    
    return parts.length > 0 ? parts.join('，') + '，' : '';
  }
  
  /**
   * 提取动作描述
   */
  private extractAction(shall: string): string {
    // 移除常见的模态动词
    let action = shall
      .replace(/^(must|should|could|will|can)\s+/i, '')
      .replace(/^be\s+able\s+to\s+/i, '')
      .replace(/^be\s+/i, '');
    
    // 转换为中文友好的描述
    if (action.startsWith('NOT ')) {
      action = `禁止${action.substring(4)}`;
    }
    
    return action;
  }
  
  /**
   * 获取测试描述
   */
  private getTestDescription(req: EARSRequirement): string {
    if (req.type === EARSType.UNWANTED) {
      return `验证 ${req.system} 不会 ${req.shall.replace('NOT ', '')}`;
    }
    
    const context = this.getContext(req);
    return `${context}验证 ${req.system} ${req.shall}`;
  }
  
  /**
   * 获取简要描述
   */
  private getSummary(req: EARSRequirement): string {
    const action = this.extractAction(req.shall);
    return action.length > 30 ? action.substring(0, 30) + '...' : action;
  }
  
  /**
   * 映射优先级
   */
  private mapPriority(priority?: 'must' | 'should' | 'could'): TaskPriority | undefined {
    const mapping: Record<string, TaskPriority> = {
      'must': 'high',
      'should': 'medium',
      'could': 'low'
    };
    
    return priority ? mapping[priority] : undefined;
  }
  
  /**
   * 估算工时
   */
  private estimateHours(req: EARSRequirement): number {
    let hours = 4; // 基础工时
    
    // 根据类型调整
    switch (req.type) {
      case EARSType.COMPLEX:
        hours = 16;
        break;
      case EARSType.EVENT_DRIVEN:
      case EARSType.STATE_DRIVEN:
        hours = 8;
        break;
      case EARSType.OPTIONAL:
        hours = 6;
        break;
      case EARSType.UNWANTED:
        hours = 4;
        break;
    }
    
    // 根据优先级调整
    if (req.priority === 'must') {
      hours *= 1.2;
    }
    
    // 根据条件复杂度调整
    const conditions = [req.when, req.where, req.while, req.if].filter(Boolean).length;
    hours += conditions * 2;
    
    return Math.ceil(hours);
  }
  
  /**
   * 获取任务分类
   */
  private getSection(type: EARSType): string {
    const sections = {
      [EARSType.UBIQUITOUS]: '核心功能',
      [EARSType.EVENT_DRIVEN]: '事件处理',
      [EARSType.STATE_DRIVEN]: '状态管理',
      [EARSType.OPTIONAL]: '可选功能',
      [EARSType.COMPLEX]: '复杂功能',
      [EARSType.UNWANTED]: '安全约束'
    };
    
    return sections[type] || '其他任务';
  }
  
  /**
   * 重建 EARS 语句
   */
  private reconstructEARS(req: EARSRequirement): string {
    const parts: string[] = [];
    
    if (req.when) parts.push(`WHEN ${req.when}`);
    if (req.where) parts.push(`WHERE ${req.where}`);
    if (req.while) parts.push(`WHILE ${req.while}`);
    if (req.if) parts.push(`IF ${req.if} THEN`);
    
    parts.push(`the ${req.system} shall ${req.shall}`);
    
    return parts.join(' ');
  }
}